// apps/provider/src/services/api/index.js
// ApiService extends ApiClient with all domain methods

import { ApiClient } from './client';
import { withAudit } from '../auditMiddleware';
import { installOfflineMiddleware } from '../offline';
import { getPrescriptionStatus, canRetryPrescription, needsManualIntervention } from '@/utils/prescriptionStatus';
// Phase 1 domain modules
import { createPharmacyMethods } from './pharmacy';
import { createDocumentsMethods } from './documents';
import { createVideoMethods } from './video';
import { createAuditMethods } from './audit';

// Phase 2 domain modules
import { createPatientsMethods } from './patients';
import { createClinicalMethods } from './clinical';
import { createUsersMethods } from './users';
import { createVeradigmMethods } from './veradigm';

// Phase 3 domain modules
import { createSchedulingMethods } from './scheduling';
import { createFormsMethods } from './forms';
import { createExercisesMethods } from './exercises';
import { createWorkoutPlansMethods } from './workoutPlans';
import { createRehabProgramsMethods } from './rehabPrograms';

// Phase 4 domain modules
import { createMessagingMethods } from './messaging';
import { createBillingMethods } from './billing';
import { createRTMMethods } from './rtm';
import { createScribeMethods } from './scribe';

// Phase 5 domain modules (clinical specialties & permissions)
import { createDocumentationConfigMethods } from './documentationConfig';
import { createPlanOfCareMethods } from './planOfCare';
import { createSupervisionMethods } from './supervision';
import { createSupervisionRulesMethods } from './supervisionRules';
import { createNPComplianceMethods } from './npCompliance';

// Phase 6 domain modules (flow sheet)
import { createFlowSheetMethods } from './flowSheet';
import { createFlowSheetCatalogMethods } from './flowSheetCatalog';

// Phase 7 domain modules (Google Calendar)
import { createGoogleCalendarMethods } from './googleCalendar';

// Phase 8 domain modules (Physician Orders)
import { createOrdersMethods } from './orders';

// Phase 9 domain modules (E-Fax)
import { createFaxMethods } from './fax';

// Phase 10 domain modules (Referral Sources)
import { createReferralMethods } from './referral';
// Home Health (agency episodes; solution-gated endpoints under hh/*)
import { createHomeHealthMethods } from './homeHealth';
import { createOasisMethods } from './oasis';

// Phase 11 domain modules (Reporting)
import { createReportingMethods } from './reporting';

// Phase 12 domain modules (Billing Rules)
import { createBillingRulesMethods } from './billingRules';

// WebPT self-service import wizard
import { createWebptImportMethods } from './webptImport';

// Month close package + bank reconciliation
import { createMonthCloseMethods } from './monthClose';

// Phase 13 domain modules (Notifications)
import { createNotificationsMethods } from './notifications';

// Phase 14 domain modules (Prior Authorization)
import { createPriorAuthMethods } from './priorAuth';
import { createTimeEntryMethods } from './timeEntry';
import { createMileageMethods } from './mileage';

// Phase 15 domain modules (Classes)
import { createClassesMethods } from './classes';

// Phase 16 domain modules (Veradigm PM Billing)
import { createVeradigmPMMethods } from './veradigmPM';
// ModMed PM billing (2026-11-04)
import { createModMedMethods } from './modmed';

// Phase 17 domain modules (Eligibility)
import { createEligibilityMethods } from './eligibility';

// Phase 18 domain modules (Support)
import { createSupportMethods } from './support';
import { createWaitlistMethods } from './waitlist';

// Phase 19 domain modules (Problem List)
import { createProblemListMethods } from './problemList';

// Phase 20 domain modules (Email Broadcasts)
import { createBroadcastsMethods } from './broadcasts';

// Phase 21 domain modules (Services Admin)
import { createServicesMethods } from './services';

// Phase 22 domain modules (Products & Inventory)
import { createProductsMethods } from './products';

// Phase 23 domain modules (Document Scanning)
import { createScanSessionMethods } from './scanSession';

// Phase 25 domain modules (Admin Merge Operations)
import { createAdminMethods } from './admin';

// Phase 26 domain modules (PTO Requests)
import { createPtoRequestMethods } from './ptoRequest';

// Persisted patient alerts (form answer alerts)
import { createAlertMethods } from './alerts';
// Patient feedback dashboard
import { createFeedbackMethods } from './feedback';

class ApiService extends ApiClient {

  // Users — extracted to ./users.js
  // Patients — extracted to ./patients.js

  // ICD-10 Codes
  async searchICD10Codes(query) {
    return this.request(`/search/icd10-codes?query=${encodeURIComponent(query)}`, {
      method: 'GET'
    });
  }

  async validateICD10Codes(codes) {
    return this.request('/icd10-codes/validate', {
      method: 'POST',
      body: JSON.stringify({ codes }),
    });
  }

  // Favorite ICD-10 codes (per-user, denormalized code + description)
  // Xano: user_favorite_icd10_codes_{GET,POST,id_DELETE}.xs (both envs)
  async getFavoriteICD10Codes() {
    return this.request('/user_favorite_icd10_codes', { method: 'GET' });
  }

  async addFavoriteICD10Code(code, description) {
    return this.request('/user_favorite_icd10_codes', {
      method: 'POST',
      body: JSON.stringify({ code, description }),
    });
  }

  async removeFavoriteICD10Code(recordId) {
    return this.request(`/user_favorite_icd10_codes/${recordId}`, { method: 'DELETE' });
  }

  // CPT Codes
  async searchCPTCodes(query, category) {
    const params = new URLSearchParams({ query });
    if (category) params.set('category', category);
    return this.request(`/search/cpt-codes?${params.toString()}`, { method: 'GET' });
  }

  async getCPTModifiers() {
    return this.request('/cpt-modifiers', { method: 'GET' });
  }

  // Medications (drug data served by the self-hosted SageRx API via Xano; replaced FDB 2026-09)
  async searchMedications(searchText) {
    return this.request(`/sagerx/prescribable-drugs?searchText=${encodeURIComponent(searchText)}`, {
      method: 'GET'
    });
  }
  // get most restrictive schedule
  async getMostRestrictiveSchedule(drugId, states) {
    const queryParams = new URLSearchParams();
    queryParams.append('drug_id', drugId);
    if (states) {
      queryParams.append('states', states);
    }

    return this.request(`/sagerx/most-restrictive-schedule?${queryParams.toString()}`, {
      method: 'GET'
    });
  }

  // Pharmacy — extracted to ./pharmacy.js

  // Sigs

  // get sigs
  async getSigs(prescribableDrugId) {
    return this.request(`/sagerx/sigs?drug_id=${encodeURIComponent(prescribableDrugId)}`, {
      method: 'GET'
    });
  }

  // get frequency and duration from typed sig
  async getSigData(instructions) {
    return this.request(`/extract_sig_data?instructions=${encodeURIComponent(instructions)}`, {
      method: 'GET'
    });
  }

  // Prescription

  async createPrescription(prescriptionData) {
    try {
      const response = await this.request('/test_prescription_signature', {
        method: 'POST',
        body: JSON.stringify(prescriptionData),
      }, 'transmitPrescription');

      // Check if this is a batch response (multiple medications)
      if (response.results && Array.isArray(response.results)) {
        // New batch response format
        return {
          status: response.status,
          message: response.message,
          results: response.results,
          summary: response.summary,
          rawResponse: response
        };
      }

      // Single medication response (legacy format)
      if (response.Status) {
        const statusInfo = getPrescriptionStatus(response);

        return {
          ...statusInfo,
          prescriptionId: response.prescription_id,
          rawResponse: response
        };
      }

      // If no recognizable response format, treat as error
      return {
        status: 'error',
        message: 'No response received from prescription service',
        isRetryable: true,
        severity: 'error',
        details: response
      };
    } catch (error) {
      console.error('Prescription creation error:', error);

      // Handle network or system errors
      return {
        status: 'error',
        message: 'Failed to send prescription. Please try again.',
        isRetryable: true,
        severity: 'error',
        details: error.message
      };
    }
  }
  // Get prescription history
  async getPrescriptionHistory(userId) {
    return this.request(`/prescription?user_id=${userId}`, {
      method: 'GET'
    });
  }
  // Get prescription detail
  async getPrescriptionDetail(prescriptionId) {
    return this.request(`/prescription/${prescriptionId}`, {
      method: 'GET'
    });
  }

  // Cancel prescription
  async cancelPrescription(cancelData, retryCount = 0) {
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 1000; // 1 second delay between retries

    try {
      // Create a cancellation payload that includes any pharmacy_ncpdp_id if present
      const payload = {
        ...cancelData,
        // If a pharmacy_ncpdp_id was provided, include it explicitly
        ...(cancelData.pharmacy_ncpdp_id ? { pharmacy_ncpdp_id: cancelData.pharmacy_ncpdp_id } : {})
      };

      const response = await this.request('/prescription_cancel_request', {
        method: 'POST',
        body: JSON.stringify(payload)
      }, 'cancelPrescription');

      // Process the response to determine if we received an acknowledgment
      if (response.Status && (response.Status.Code === '000' || response.Status.Code === '010')) {
        return response;
      } else {
        // We received a response, but not a positive acknowledgment
        console.error('Cancel request not acknowledged:', response);

        // If we haven't reached max retries, try again
        if (retryCount < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
          return this.cancelPrescription(cancelData, retryCount + 1);
        }

        return {
          success: false,
          message: response.Status?.Description || 'Cancellation request failed.',
          rawResponse: response
        };
      }
    } catch (error) {
      console.error('Error sending cancel request:', error);

      // If we haven't reached max retries, try again
      if (retryCount < MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return this.cancelPrescription(cancelData, retryCount + 1);
      }

      throw error;
    }
  }

  // Get change requests
  async getChangeRequests(userId) {
    return this.request(`/prescription_change_request?user_id=${userId}`, {
      method: 'GET'
    });
  }

  // Get change request detail - you would use this as a fallback if not passed from list view
  async getChangeRequestDetail(changeRequestId) {
    try {
      // First, try to get the specific change request detail
      return this.request(`/prescription_change_request/${changeRequestId}`, {
        method: 'GET'
      });
    } catch (error) {
      console.error('Error fetching change request detail:', error);

      // Fallback: fetch all change requests and find the matching one
      const allRequests = await this.getChangeRequests();
      const changeRequest = allRequests.find(req => req.id === changeRequestId);

      if (!changeRequest) {
        throw new Error('Change request not found');
      }

      return changeRequest;
    }
  }

  // Respond to a change request
  async respondToChangeRequest(responseData) {
    // Ensure the response includes the denial_reason_code if denying
    if (responseData.status === 'denied' && responseData.denial_reason_code) {
    }

    return this.request('/prescription_change_request', {
      method: 'POST',
      body: JSON.stringify(responseData)
    }, 'respondToChangeRequest');
  }

  // Get renewal requests
  async getRenewalRequests(userId) {
    return this.request(`/prescription_renewal_request?user_id=${userId}`, {
      method: 'GET'
    });
  }


  // Get details for a specific renewal request
  async getRenewalRequestDetail(renewalRequestId) {
    return this.request(`/prescription_renewal_request/${renewalRequestId}`, {
      method: 'GET'
    });
  }
  // Respond to renewal request
  async respondToRenewalRequest(response) {
    return this.request('/prescription_renewal_request', {
      method: 'POST',
      body: JSON.stringify(response)
    }, 'respondToRenewalRequest');
  }

  // Get renewal request count for notifications/badges
  async getPendingRenewalRequestCount(userId) {
    try {
      const renewalRequests = await this.getRenewalRequests(userId);
      return renewalRequests.filter(request => request.status === 'pending').length;
    } catch (error) {
      console.error('Error fetching pending renewal request count:', error);
      return 0;
    }
  }

  // Get change request count for notifications/badges
  async getPendingChangeRequestCount(userId) {
    try {
      const changeRequests = await this.getChangeRequests(userId);
      return changeRequests.filter(request => request.status === 'pending').length;
    } catch (error) {
      console.error('Error fetching pending renewal request count:', error);
      return 0;
    }
  }

  // Get cancel requests
  async getCancelRequests(userId) {
    return this.request(`/prescription_cancel_request?user_id=${userId}`, {
      method: 'GET'
    });
  }

  // Get prescriptions for a patient
  async getPrescriptions(patientId) {
    return this.request(
      `/prescriptions/patient?patient_id=${patientId}`,
      { method: 'GET' }
    );
  }

  // Veradigm — extracted to ./veradigm.js

  // Favorites management
  async getFavoriteMedications(userId) {
    if (!userId) return [];

    return this.request(`/favorite_medications?user_id=${userId}`, {
      method: 'GET'
    });
  }

  // Add a medication to favorites (with complete medication data)
  async addFavoriteMedication(userId, medicationId, medicationData) {
    return this.request('/favorite_medications', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        medication_id: medicationId,
        medication_data: medicationData // Store the complete medication object
      })
    });
  }

  // Remove a medication from favorites
  async removeFavoriteMedication(userId, medicationId) {
    return this.request(`/favorite_medications`, {
      method: 'DELETE',
      body: JSON.stringify({
        user_id: userId,
        medication_id: medicationId
      })
    });
  }

  // Check if a medication is in favorites
  async isFavoriteMedication(userId, medicationId) {

    try {
      const favorites = await this.getFavoriteMedications(userId);
      const isFavorite = favorites.some(fav => fav.medication_id === medicationId);
      return isFavorite;
    } catch (error) {
      console.error('❌ API: Error checking favorite status:', error);
      throw error;
    }
  }

  // Veradigm auth, users, organizations — extracted to ./veradigm.js and ./users.js

  // Audit reports — extracted to ./audit.js

  async verifyPin(userId, pin) {
    return this.request('/erx/verify-pin', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, pin })
    }, 'verifyPin');
  }

  // Exercises (search, filters, favorites, prescriptions, media, AI generation, batch) — extracted to ./exercises.js

  // Workout Plans (CRUD, weeks, schedules, composites) — extracted to ./workoutPlans.js

  // Rehab Programs (CRUD, phases, prescriptions, patient exercise assignments, composites) — extracted to ./rehabPrograms.js

  // Form Builder (definitions, versions, publishing, responses, reports, attachments, public forms,
  // patient forms, form-to-patient mapping, redirect validation) — extracted to ./forms.js

  // Form-based scheduling (updateScheduleResponse, getSchedulingRequests, checkSlotsAvailability, confirmSchedulingRequest) — extracted to ./scheduling.js

  // Locations, organizations, solutions — extracted to ./users.js

  // uploadExerciseMedia, uploadExerciseFromUrl — extracted to ./exercises.js

  // testFormDataUpload — extracted to ./forms.js

  // Recent Activity
  async getRecentActivities(organizationId, locationId = null, before = null, limit = 10) {
    let endpoint = `/recent_activity?organization_id=${organizationId}&solution_id=1`;
    if (locationId) endpoint += `&location_id=${locationId}`;
    if (before) endpoint += `&before=${before}`;

    return this.request(endpoint, { method: 'GET' });
  }

  async logActivity(userId, organizationId, locationId, activityType, entityType, entityId, description, metadata, status, link) {
    return this.request('/recent_activity', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        organization_id: organizationId,
        location_id: locationId,
        activity_type: activityType,
        entity_type: entityType,
        entity_id: entityId,
        description,
        metadata,
        status,
        link,
        visibility: null,
        solution_id: 1
      })
    });
  }

  // Support Requests — extracted to ./support.js

  // Feedback
  async getFeedback(organizationId) {
    return this.request(`/guidept_feedback?organization_id=${organizationId}`, { method: 'GET' });
  }

  async submitFeedback(feedback) {
    return this.request('/guidept_feedback', {
      method: 'POST',
      body: JSON.stringify({
        ...feedback,
        status: 'pending'
      })
    });
  }

  // Patient verification — extracted to ./patients.js
  // Encounters, clinical notes, vitals — extracted to ./clinical.js

  // Patient forms, reports, form-to-patient mapping — extracted to ./forms.js

  // Scheduling (availability, appointments, overrides, slots, self-scheduling) — extracted to ./scheduling.js

  // Patient update queue — extracted to ./patients.js

  // Schedule summaries — extracted to ./scheduling.js

  // Patient matching, search — extracted to ./patients.js

  // Messaging (threads, messages, participants, attachments, search, unread counts) — extracted to ./messaging.js

  // Video Sessions — extracted to ./video.js

  // validateFormRedirect, getQuestionTypeCompatibility — extracted to ./forms.js

  // Documents — extracted to ./documents.js

  // getPatientRecentVitals — extracted to ./clinical.js

  // getUpcomingAppointments — extracted to ./scheduling.js

  // getPatientDiagnoses — extracted to ./patients.js

  // Billing, invoices, service packages, subscription plans, patient packages,
  // superbills, credits — extracted to ./billing.js

  // PrismScribe (recordings, transcription, AI documentation, templates) — extracted to ./scribe.js

  // RTM (enrollments, billing periods, management activities, interactions, dashboard, alerts, patients) — extracted to ./rtm.js

  // Lab orders — extracted to ./clinical.js

}

const baseApiService = new ApiService();

// Mix in domain module methods (they use `this` so they work on the instance)
// Phase 1
Object.assign(baseApiService, createPharmacyMethods());
Object.assign(baseApiService, createDocumentsMethods());
Object.assign(baseApiService, createVideoMethods());
Object.assign(baseApiService, createAuditMethods());
// Phase 2
Object.assign(baseApiService, createPatientsMethods());
Object.assign(baseApiService, createClinicalMethods());
Object.assign(baseApiService, createUsersMethods());
Object.assign(baseApiService, createVeradigmMethods());
// Phase 3
Object.assign(baseApiService, createSchedulingMethods());
Object.assign(baseApiService, createFormsMethods());
Object.assign(baseApiService, createExercisesMethods());
Object.assign(baseApiService, createWorkoutPlansMethods());
Object.assign(baseApiService, createRehabProgramsMethods());
// Phase 4
Object.assign(baseApiService, createMessagingMethods());
Object.assign(baseApiService, createBillingMethods());
Object.assign(baseApiService, createRTMMethods());
Object.assign(baseApiService, createScribeMethods());
// Phase 5
Object.assign(baseApiService, createDocumentationConfigMethods());
Object.assign(baseApiService, createPlanOfCareMethods());
Object.assign(baseApiService, createSupervisionMethods());
Object.assign(baseApiService, createSupervisionRulesMethods());
Object.assign(baseApiService, createNPComplianceMethods());
// Phase 6
Object.assign(baseApiService, createFlowSheetMethods());
Object.assign(baseApiService, createFlowSheetCatalogMethods());
// Phase 7
Object.assign(baseApiService, createGoogleCalendarMethods());
// Phase 8
Object.assign(baseApiService, createOrdersMethods());
// Phase 9
Object.assign(baseApiService, createFaxMethods());
// Phase 10
Object.assign(baseApiService, createReferralMethods());
// Phase 11
Object.assign(baseApiService, createReportingMethods());
// Phase 12
Object.assign(baseApiService, createBillingRulesMethods());
// Phase 13
Object.assign(baseApiService, createNotificationsMethods());
// Phase 14
Object.assign(baseApiService, createPriorAuthMethods());
// Time tracking
Object.assign(baseApiService, createTimeEntryMethods());
// Mileage tracking
Object.assign(baseApiService, createMileageMethods());
// Phase 15 — Classes
Object.assign(baseApiService, createClassesMethods());
// Phase 16 — Veradigm PM Billing
Object.assign(baseApiService, createVeradigmPMMethods());
// ModMed PM Billing
Object.assign(baseApiService, createModMedMethods());
// Phase 17 — Eligibility
Object.assign(baseApiService, createEligibilityMethods());
// Phase 18 — Support
Object.assign(baseApiService, createSupportMethods());
// Phase 19 — Waitlist
Object.assign(baseApiService, createWaitlistMethods());
// Phase 20 — Problem List
Object.assign(baseApiService, createProblemListMethods());
// Phase 21 — Email Broadcasts
Object.assign(baseApiService, createBroadcastsMethods());
// Phase 22 — Services Admin
Object.assign(baseApiService, createServicesMethods());
// Phase 23 — Products & Inventory
Object.assign(baseApiService, createProductsMethods());
// Phase 24 — Document Scanning
Object.assign(baseApiService, createScanSessionMethods());
// Phase 25 — Admin Merge Operations
Object.assign(baseApiService, createAdminMethods());
// Phase 26 — PTO Requests
Object.assign(baseApiService, createPtoRequestMethods());
// WebPT self-service import wizard
Object.assign(baseApiService, createWebptImportMethods());
Object.assign(baseApiService, createMonthCloseMethods());
// Persisted patient alerts (form answer alerts)
Object.assign(baseApiService, createAlertMethods());
// Patient feedback dashboard
Object.assign(baseApiService, createFeedbackMethods());
// Home Health
Object.assign(baseApiService, createHomeHealthMethods());
Object.assign(baseApiService, createOasisMethods());

// Order matters: the offline layer must sit INSIDE the audit wrapper
// (audit → offline → client). A mutation diverted to the offline queue then
// resolves with its local echo, so the audit wrapper records the action that
// actually happened (locally, replayed later) instead of emitting a spurious
// failure entry; queue replays go through the raw client request and are not
// double-audited. With offline mode disabled the offline layer is a pure
// passthrough, so this ordering is behavior-neutral for every other org.
installOfflineMiddleware(baseApiService);
export const apiService = withAudit(baseApiService);